/**
 * KDL-715: Simple Iframe Focus Helper
 *
 * Provides simple functions for focusing iframes when needed
 */

// Track original body pointer-events to restore later
let originalBodyPointerEvents: string | null = null;

// Safety mechanism: restore pointer events on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    restoreBodyPointerEvents();
  });
}

/**
 * Temporarily removes pointer-events: none from body to allow iframe interaction
 */
export function enableBodyPointerEvents(): void {
  const body = document.body;
  // Always save the current state, regardless of its value
  originalBodyPointerEvents = body.style.pointerEvents || '';
  if (body.style.pointerEvents === 'none') {
    body.style.pointerEvents = 'auto';
  }
}

/**
 * Restores body pointer-events after the Google Picker iframe workaround.
 * Radix Dialog sets `pointer-events: none` on `body` while open; saving and restoring that
 * value once the workaround ends leaves the page stuck non-interactive after the modal closes.
 * Clear the inline property when we had forced `auto` over `none` (or an empty inline value).
 */
export function restoreBodyPointerEvents(): void {
  const body = document.body;
  if (originalBodyPointerEvents !== null) {
    if (originalBodyPointerEvents === 'none' || originalBodyPointerEvents === '') {
      body.style.removeProperty('pointer-events');
    } else {
      body.style.pointerEvents = originalBodyPointerEvents;
    }
    originalBodyPointerEvents = null;
  }
}

/**
 * Monitors iframe and restores body pointer events when iframe is removed
 */
export function monitorIframeAndRestore(selector: string): void {
  const checkIframe = () => {
    const iframe = document.querySelector(selector);
    if (!iframe) {
      // Iframe is gone, restore body pointer events
      restoreBodyPointerEvents();
    } else {
      // Iframe still exists, check again in 100ms
      setTimeout(checkIframe, 100);
    }
  };

  // Start monitoring
  setTimeout(checkIframe, 100);
}

// Track iframes that already have event listeners
const iframesWithListeners = new WeakSet<HTMLIFrameElement>();

// Event handlers
const handleLoad = (iframe: HTMLIFrameElement) => () => iframe.focus();
const handleClick = (iframe: HTMLIFrameElement) => () => iframe.focus();

/**
 * Focuses an iframe by selector (ID, class, or CSS selector)
 * Call this when you know an iframe exists or will exist soon
 *
 * @param selector - CSS selector for the iframe (e.g., '#my-iframe', '.picker-frame', 'iframe[data-type="auth"]')
 * @param retries - Number of times to retry if iframe not found (default: 5)
 * @param delay - Delay between retries in ms (default: 100)
 */
export function focusIframe(selector: string, retries: number = 5, delay: number = 100): void {
  const attempt = () => {
    const iframe = document.querySelector(selector) as HTMLIFrameElement;

    if (iframe) {
      // Apply focus improvements
      iframe.style.pointerEvents = 'auto';
      iframe.setAttribute('tabindex', '0');
      iframe.focus();

      // Try to focus content window
      try {
        iframe.contentWindow?.focus();
      } catch (error) {
        // Expected due to CORS
      }

      // Add event listeners for better interaction, only once per iframe
      if (!iframesWithListeners.has(iframe)) {
        const loadHandler = handleLoad(iframe);
        const clickHandler = handleClick(iframe);

        iframe.addEventListener('load', loadHandler);
        iframe.addEventListener('click', clickHandler);

        // Store cleanup function in iframe's dataset
        iframe.dataset.cleanupListeners = JSON.stringify({
          load: loadHandler,
          click: clickHandler,
        });

        iframesWithListeners.add(iframe);
      }

      return; // Success, exit
    }

    // Retry if iframe not found and retries left
    if (retries > 0) {
      setTimeout(() => focusIframe(selector, retries - 1, delay), delay);
    }
  };

  // Start first attempt
  setTimeout(attempt, delay);
}

/**
 * Focuses multiple iframe selectors (for multi-step flows like OAuth + File Picker)
 * Call this when you want to handle different iframes that may appear at different times
 *
 * @param selectors - Array of CSS selectors to try
 * @param maxTime - Maximum time to keep trying in ms (default: 120000 = 2 minutes)
 */
export function focusIframeFlow(selectors: string[], maxTime: number = 120000): void {
  let startTime = Date.now();
  let focusedSelectors = new Set<string>();

  const checkIframes = () => {
    // Stop if time exceeded
    if (Date.now() - startTime > maxTime) {
      return;
    }

    // Check each selector
    for (const selector of selectors) {
      if (!focusedSelectors.has(selector)) {
        const iframe = document.querySelector(selector) as HTMLIFrameElement;
        if (iframe) {
          focusIframe(selector, 1, 0); // Focus immediately, no retries needed
          focusedSelectors.add(selector);
        }
      }
    }

    // Continue checking if not all selectors found
    if (focusedSelectors.size < selectors.length) {
      setTimeout(checkIframes, 200);
    }
  };

  checkIframes();
}
